import { defineMessages } from 'react-intl';
import type {
  Token,
  ClassificationMark,
  ClassificationResult,
  MarkOrderArray,
  ClassificationMarkError,
} from './types';
import {
  markCategoryOrder,
  MarkCategoriesEnum,
  LevelValueEnum,
  MarkTypeEnum,
  ParsingOperatorsEnum,
  ClassificationOk,
} from './types';
import { standardLevels, natoLevels } from './levels';
import standaloneMarks from './standaloneMarks';
import { lex } from './lexer';

const messages = defineMessages({
  improperDash: {
    id: 'messages.improperDash',
    defaultMessage: 'Classification has improper dash.',
  },
  outOfOrder: {
    id: 'messages.outOfOrder',
    defaultMessage:
      'Mark control order is invalid: {control} should not appear before {nextCategory}.',
  },
  malformedMark: {
    id: 'messages.malformedMark',
    defaultMessage: 'Malformed classification mark.',
  },
  undeterminedLevel: {
    id: 'messages.undeterminedLevel',
    defaultMessage: 'Could not determine classification level.',
  },
  invalidRelTo: {
    id: 'messages.invalidRelTo',
    defaultMessage: `Could not parse REL TO values.`,
  },
  invalidAccm: {
    id: 'messages.invalidAccm',
    defaultMessage: `Could not parse ACCM values.`,
  },
  invalidSigma: {
    id: 'messages.invalidSigma',
    defaultMessage: `Could not parse SIGMA values.`,
  },
  invalidSci: {
    id: 'messages.invalidSci',
    defaultMessage: `Could not parse SCI values.`,
  },
  invalidSap: {
    id: 'messages.invalidSap',
    defaultMessage: `Could not parse SAP values.`,
  },
  invalidFgi: {
    id: 'messages.invalidFgi',
    defaultMessage: `Could not parse FGI values.`,
  },
  invalidDisplayOnly: {
    id: 'messages.invalidDisplayOnly',
    defaultMessage: `Could not parse DISPLAY ONLY string.`,
  },
  lowercaseLetters: {
    id: 'messages.lowercaseLetters',
    defaultMessage: `Classification mark should contain only uppercase letters.`,
  },
  extraSlashes: {
    id: 'messages.extraSlashes',
    defaultMessage: `Classification mark contain only single slashes (/) and/or double-slashes (//).`,
  },
  emptyControls: {
    id: 'messages.emptyControls',
    defaultMessage: `Classification mark contains one or more empty control values (no controls between slashes).`,
  },
  invalidCharacters: {
    id: 'messages.invalidCharacters',
    defaultMessage: `Classification mark contains invalid characters.`,
  },
  hangingSlash: {
    id: 'messages.hangingSlash',
    defaultMessage: `Classification mark has a hanging slash.`,
  },
});

function checkMarkCase(mark: string): Array<ClassificationMarkError> {
  return /[a-z]/.test(mark) ? [messages.lowercaseLetters] : [];
}

function checkExtraSlashes(mark: string): Array<ClassificationMarkError> {
  return /\/{3,}/.test(mark) ? [messages.extraSlashes] : [];
}

function checkEmptyControls(mark: string): Array<ClassificationMarkError> {
  return /\/[ ]+\//.test(mark) || /\/[ ]+[){0,1}]$/.test(mark) ? [messages.emptyControls] : [];
}

function checkInvalidCharacters(mark: string): Array<ClassificationMarkError> {
  return /[^A-Z0-9-() /,]/.test(mark) ? [messages.invalidCharacters] : [];
}

function checkHangingSlash(mark: string): Array<ClassificationMarkError> {
  return mark.endsWith('/') || mark.endsWith('/)') ? [messages.hangingSlash] : [];
}

function checkMarkOrder(markOrder: MarkOrderArray): Array<ClassificationMarkError> {
  const itemsOutOfOrder: Array<ClassificationMarkError> = [];

  // Iterate over markOrder and compare it to the desired order in markCategoryOrder
  for (let i = 0; i < markOrder.length - 1; i += 1) {
    const { category, control } = markOrder[i]!;

    let j = i + 1;
    let nextCategory = markOrder[j]!.category;

    while (nextCategory === category && j < markOrder.length - 1) {
      j += 1;
      nextCategory = markOrder[j]!.category;
    }

    // Find the index of the current and next categories in markCategoryOrder
    const currentCategoryIndex = markCategoryOrder.indexOf(category);
    const nextCategoryIndex = markCategoryOrder.indexOf(nextCategory);

    // If the next category index is smaller than the current category index, it is out of order
    if (nextCategoryIndex < currentCategoryIndex) {
      itemsOutOfOrder.push({
        ...messages.outOfOrder,
        values: {
          control,
          nextCategory,
        },
      });
    }
  }

  return itemsOutOfOrder;
}

function collectOrgs(_tokens: Token[]): { orgs: string[]; tokens: Token[] } {
  const tokens = [..._tokens];
  const orgs: string[] = [];
  let token = tokens.shift();
  while (token && (token[0] === 'organization' || token[0] === 'unknown')) {
    const [, value] = token;
    orgs.push(value);
    token = tokens.shift();
  }

  // the ternary operator here is to make sure we replace the token
  // we did not consume back into the tokens array
  return { orgs, tokens: [...(token ? [token] : []), ...tokens] };
}

function parseClassificationCategory(
  _tokens: Array<Token>
): ClassificationMark & { remainingTokens: Token[] } {
  const warnings: Array<ClassificationMarkError> = [];
  if (!_tokens.length) {
    return { warnings: [messages.malformedMark], remainingTokens: [] };
  }

  const filteredTokens = _tokens.filter((token) => token[1] !== '//' && token[1] !== '/');

  if (filteredTokens.length === 1 && filteredTokens[0]![0] === 'unknown') {
    return {
      type: MarkTypeEnum.DOMESTIC,
      warnings: [messages.undeterminedLevel],
      remainingTokens: [],
    };
  }

  if (filteredTokens.length === 1 && filteredTokens[0]![0] === 'operator') {
    const standalone = standaloneMarks[filteredTokens[0]![1]];
    if (standalone) {
      return { ...standalone, remainingTokens: [] };
    }
    return {
      type: MarkTypeEnum.DOMESTIC,
      warnings: [messages.undeterminedLevel],
      remainingTokens: _tokens,
    };
  }

  let tokens = [..._tokens];
  const fgi = new Array<string>();

  let markingType = MarkTypeEnum.DOMESTIC;
  let classificationLevel: LevelValueEnum | null = null;
  let atomal = false;
  let bohemia = false;
  let cui = false;
  let dcni = false;
  let hasFgi = false;

  if (tokens[0]![1] === '//') {
    tokens.shift();
    markingType = MarkTypeEnum.FOREIGN;
  }

  let token = tokens.shift();

  while (token && token[1] !== '//') {
    const [type, value] = token;

    if (type === 'classification') {
      const level = standardLevels.find(({ portion }) => portion === value);

      if (level) {
        classificationLevel = level.value;
      }

      const natoLevel = natoLevels.find(({ portion }) => portion === value);

      if (natoLevel) {
        classificationLevel = natoLevel.value;
        markingType = MarkTypeEnum.NATO;
        if (natoLevel.modifier === '-A') atomal = true;
        if (natoLevel.modifier === '-B') bohemia = true;
        hasFgi = true;
        fgi.push('NATO');
      }

      if (tokens[0] && tokens[0][1] === ParsingOperatorsEnum.DASH && tokens.length === 1) {
        warnings.push(messages.improperDash);
      }
    } else if (type === 'organization') {
      const orgsResult = collectOrgs(tokens);
      tokens = orgsResult.tokens;
      hasFgi = true;
      fgi.push(value);
      fgi.push(...orgsResult.orgs);
    } else if (value === 'JOINT') {
      markingType = MarkTypeEnum.JOINT;
      hasFgi = true;
    } else if (value === 'FGI') {
      hasFgi = true;
    } else if (value === 'CUI') {
      classificationLevel = LevelValueEnum.UNCLASSIFIED;
      cui = true;
    } else if (value === 'DCNI') {
      classificationLevel = LevelValueEnum.UNCLASSIFIED;
      dcni = true;
    } else if (classificationLevel !== undefined) {
      const orgsResult = collectOrgs(tokens);
      tokens = orgsResult.tokens;
      hasFgi = true;
      fgi.push(value);
      fgi.push(...orgsResult.orgs);
    } else {
      warnings.push(messages.malformedMark);
      // if we couldn't parse the token, put it back so additional parse logic can try
      tokens.unshift(token);
      break;
    }

    token = tokens.shift();
  }

  // If the portion mark contains no classification level, add a warning
  if (classificationLevel === null) {
    warnings?.push(messages.undeterminedLevel);
  }

  const classificationMark: ClassificationMark & { remainingTokens: Token[] } = {
    remainingTokens: tokens,
    type: markingType,
    warnings,
  };

  if (classificationLevel !== null) classificationMark.classificationLevel = classificationLevel;
  if (atomal) classificationMark.atomal = true;
  if (bohemia) classificationMark.bohemia = true;
  if (cui) classificationMark.cui = true;
  if (dcni) classificationMark.dcni = true;
  if (hasFgi) classificationMark.fgi = fgi;

  return classificationMark;
}

function collectMarks(
  currentValue: string | null,
  _tokens: Token[]
): ClassificationResult<{ marks: string[]; tokens: Token[] }> {
  const tokens = [..._tokens];
  const marks: string[] = [];

  if (currentValue) marks.push(currentValue);
  let token = tokens.shift();

  while (token && token[1] !== ParsingOperatorsEnum.DOUBLE_SLASH) {
    const [, value] = token;
    if (value !== ParsingOperatorsEnum.SINGLE_SLASH) {
      marks.push(value);
    }

    token = tokens.shift();
  }

  // the ternary operator here is to make sure we replace the token
  // we did not consume back into the tokens array
  return ClassificationOk({ marks, tokens: [...(token ? [token] : []), ...tokens] });
}

function collectSciMarks(
  currentValue: string | null,
  _tokens: Token[]
): ClassificationResult<{ marks: string[]; tokens: Token[] }> {
  const tokens = [..._tokens];
  const marks: string[] = [];

  let token = tokens.shift();
  let sciMark: Array<string> = [];

  if (currentValue) sciMark.push(currentValue);

  while (token && token[1] !== ParsingOperatorsEnum.DOUBLE_SLASH) {
    const [, value] = token;
    if (value !== ParsingOperatorsEnum.SINGLE_SLASH) {
      sciMark.push(value);
    } else {
      marks.push(sciMark.join(' '));
      sciMark = [];
    }

    token = tokens.shift();
  }

  if (sciMark.length) {
    marks.push(sciMark.join(' '));
  }

  // the ternary operator here is to make sure we replace the token
  // we did not consume back into the tokens array
  return ClassificationOk({ marks, tokens: [...(token ? [token] : []), ...tokens] });
}

/**
 * Parses a classification mark string into a ClassificationMark object.
 * @param {string} _mark - The classification mark string to parse.
 * @returns {ClassificationMark} - The parsed classification mark.
 */
function parseClassification(_mark: string): ClassificationMark {
  const mark = _mark.replace(/[{()}]/g, '').toUpperCase();
  const lexResult = lex(mark);
  const markOrder: MarkOrderArray = [];

  let { tokens } = lexResult;
  const tokenCount = tokens.length;

  // handle classification category
  const parseClassificationCategoryResult = parseClassificationCategory(tokens);

  const { warnings, remainingTokens, ...parsedMark } = parseClassificationCategoryResult;

  tokens = remainingTokens;

  const classificationMark: ClassificationMark = {
    ...parsedMark,
    warnings: [...(warnings ?? []), ...lexResult.warnings],
  };

  const isFirstToken = tokenCount === tokens.length;
  let pastSciMarkings = false;

  let leadingToken = tokens.shift();

  while (leadingToken) {
    const [tokenType, value] = leadingToken;

    switch (value) {
      case ParsingOperatorsEnum.TK: {
        if (!classificationMark.sci) classificationMark.sci = [];
        classificationMark.sci.push(ParsingOperatorsEnum.TK);
        markOrder.push({ control: 'TK', category: MarkCategoriesEnum.SCI });
        break;
      }
      case ParsingOperatorsEnum.TK_G: {
        if (!classificationMark.sci) classificationMark.sci = [];
        classificationMark.sci.push(ParsingOperatorsEnum.TK_G);
        markOrder.push({ control: 'TK-G', category: MarkCategoriesEnum.SCI });
        break;
      }
      case ParsingOperatorsEnum.HCS: {
        if (!classificationMark.sci) classificationMark.sci = [];
        classificationMark.sci.push(ParsingOperatorsEnum.HCS);
        markOrder.push({ control: 'HCS', category: MarkCategoriesEnum.SCI });
        break;
      }
      case ParsingOperatorsEnum.SI: {
        if (!classificationMark.sci) classificationMark.sci = [];
        classificationMark.sci.push(ParsingOperatorsEnum.SI);
        markOrder.push({ control: 'SI', category: MarkCategoriesEnum.SCI });
        break;
      }
      case ParsingOperatorsEnum.REL_TO: {
        const relToResult = collectOrgs(tokens);
        tokens = relToResult.tokens;
        classificationMark.relTo = relToResult.orgs;
        pastSciMarkings = true;
        markOrder.push({ control: 'REL TO', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        break;
      }
      case ParsingOperatorsEnum.REL: {
        const relToResult = collectOrgs(tokens);
        tokens = relToResult.tokens;
        classificationMark.relTo = relToResult.orgs;
        pastSciMarkings = true;
        markOrder.push({ control: 'REL TO', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        break;
      }
      case ParsingOperatorsEnum.DISPLAY_ONLY: {
        const displayOnlyResult = collectOrgs(tokens);
        tokens = displayOnlyResult.tokens;
        classificationMark.displayOnly = displayOnlyResult.orgs;
        pastSciMarkings = true;
        markOrder.push({ control: 'DISPLAY ONLY', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        break;
      }
      case ParsingOperatorsEnum.FGI: {
        const fgiResult = collectOrgs(tokens);
        tokens = fgiResult.tokens;
        classificationMark.fgi = fgiResult.orgs;
        pastSciMarkings = true;
        markOrder.push({ control: 'FGI', category: MarkCategoriesEnum.FGI });
        break;
      }
      case ParsingOperatorsEnum.RD: {
        classificationMark.rd = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'RD', category: MarkCategoriesEnum.AEA });
        break;
      }
      case ParsingOperatorsEnum.FRD: {
        classificationMark.frd = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'FRD', category: MarkCategoriesEnum.AEA });
        break;
      }
      case ParsingOperatorsEnum.RD_N: {
        classificationMark.rd = true;
        classificationMark.cnwdi = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'RD-N', category: MarkCategoriesEnum.AEA });
        break;
      }
      case ParsingOperatorsEnum.FRD_N: {
        classificationMark.frd = true;
        classificationMark.cnwdi = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'FRD-N', category: MarkCategoriesEnum.AEA });
        break;
      }
      case ParsingOperatorsEnum.RD_SG: {
        classificationMark.rd = true;

        const sigmaResult = collectMarks(null, tokens);

        if (!sigmaResult.ok) {
          classificationMark.warnings?.push(messages.invalidSigma);
        } else {
          tokens = sigmaResult.val.tokens;
          classificationMark.sg = sigmaResult.val.marks;
        }

        markOrder.push({ control: 'RD', category: MarkCategoriesEnum.AEA });
        markOrder.push({ control: 'SG', category: MarkCategoriesEnum.AEA });

        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.FRD_SG: {
        classificationMark.frd = true;

        const sigmaResult = collectMarks(null, tokens);

        if (!sigmaResult.ok) {
          classificationMark.warnings?.push(messages.invalidSigma);
        } else {
          tokens = sigmaResult.val.tokens;
          classificationMark.sg = sigmaResult.val.marks;
        }

        markOrder.push({ control: 'FRD', category: MarkCategoriesEnum.AEA });
        markOrder.push({ control: 'SG', category: MarkCategoriesEnum.AEA });

        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.RD_N_SG: {
        classificationMark.rd = true;
        classificationMark.nf = true;

        const sigmaResult = collectMarks(null, tokens);

        if (!sigmaResult.ok) {
          classificationMark.warnings?.push(messages.invalidSigma);
        } else {
          tokens = sigmaResult.val.tokens;
          classificationMark.sg = sigmaResult.val.marks;
        }

        markOrder.push({ control: 'RD-N', category: MarkCategoriesEnum.AEA });
        markOrder.push({ control: 'SG', category: MarkCategoriesEnum.AEA });

        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.FRD_N_SG: {
        classificationMark.frd = true;
        classificationMark.nf = true;

        const sigmaResult = collectMarks(null, tokens);

        if (!sigmaResult.ok) {
          classificationMark.warnings?.push(messages.invalidSigma);
        } else {
          tokens = sigmaResult.val.tokens;
          classificationMark.sg = sigmaResult.val.marks;
        }

        markOrder.push({ control: 'FRD-N', category: MarkCategoriesEnum.AEA });
        markOrder.push({ control: 'SG', category: MarkCategoriesEnum.AEA });

        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.SG: {
        const sigmaResult = collectMarks(null, tokens);

        if (!sigmaResult.ok) {
          classificationMark.warnings?.push(messages.invalidSigma);
        } else {
          tokens = sigmaResult.val.tokens;
          classificationMark.sg = sigmaResult.val.marks;
        }

        markOrder.push({ control: 'SG', category: MarkCategoriesEnum.AEA });

        pastSciMarkings = true;
        break;
      }
      // Simple flag operators
      case ParsingOperatorsEnum.FOUO: {
        classificationMark.fouo = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'FOUO', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        break;
      }
      case ParsingOperatorsEnum.DCNI: {
        classificationMark.dcni = true;
        pastSciMarkings = true;
        markOrder.push({ control: 'DCNI', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        break;
      }
      case ParsingOperatorsEnum.OC: {
        classificationMark.oc = true;
        markOrder.push({ control: 'OC', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.IMC: {
        classificationMark.imc = true;
        markOrder.push({ control: 'IMC', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.NF: {
        classificationMark.nf = true;

        // NF may be used with SBU, in which case it is a SECONDARY dissemination control
        if (classificationMark.sbu) {
          markOrder.push({ control: 'NF', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        } else {
          markOrder.push({ control: 'NF', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        }

        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.PR: {
        classificationMark.pr = true;
        markOrder.push({ control: 'PR', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.FISA: {
        classificationMark.fisa = true;
        markOrder.push({ control: 'FISA', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.WAIVED: {
        classificationMark.waived = true;
        markOrder.push({ control: 'WAIVED', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.RELIDO: {
        classificationMark.relido = true;
        markOrder.push({ control: 'RELIDO', category: MarkCategoriesEnum.PRIMARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.XD: {
        classificationMark.xd = true;
        markOrder.push({ control: 'XD', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.ND: {
        classificationMark.nd = true;
        markOrder.push({ control: 'ND', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.SBU: {
        classificationMark.sbu = true;
        markOrder.push({ control: 'SBU', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.SBU_NF: {
        classificationMark.sbu = true;
        classificationMark.nf = true;
        markOrder.push({ control: 'SBU-NF', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.HVSACO: {
        classificationMark.hvsaco = true;
        markOrder.push({ control: 'HVSACO', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      case ParsingOperatorsEnum.ACCM: {
        if (!classificationMark.accm) classificationMark.accm = [];
        markOrder.push({ control: 'ACCM', category: MarkCategoriesEnum.SECONDARY_DISSEM });
        pastSciMarkings = true;
        break;
      }
      default:
        if (tokenType === 'unknown') {
          const unknownMarks = collectMarks(value, tokens);
          // matches "for something only" regex
          if (
            unknownMarks.ok &&
            unknownMarks.val.marks
              .join(' ')
              .toUpperCase()
              .match(/FOR\s.*\sONLY/)
          ) {
            tokens = unknownMarks.val.tokens;
            if (!classificationMark.otherDissem) classificationMark.otherDissem = [];
            classificationMark.otherDissem.push(unknownMarks.val.marks.join(' '));
            markOrder.push({
              control: unknownMarks.val.marks.join(' '),
              category: MarkCategoriesEnum.SECONDARY_DISSEM,
            });
          } else if (value.startsWith('ACCM')) {
            if (!unknownMarks.ok) {
              classificationMark.warnings?.push(messages.invalidAccm);
            } else {
              tokens = unknownMarks.val.tokens;
              classificationMark.accm = unknownMarks.val.marks.map((accmMark) =>
                accmMark.startsWith('ACCM-') ? accmMark.replace('ACCM-', '') : accmMark
              );
              markOrder.push({ control: 'ACCM', category: MarkCategoriesEnum.SECONDARY_DISSEM });
            }
          } else if (value.startsWith('SAR')) {
            if (!unknownMarks.ok) {
              classificationMark.warnings?.push(messages.invalidSap);
            } else {
              tokens = unknownMarks.val.tokens;
              classificationMark.sap = unknownMarks.val.marks.map((sapMark) =>
                sapMark.startsWith('SAR-') ? sapMark.replace('SAR-', '') : sapMark
              );
              markOrder.push({ control: 'SAP', category: MarkCategoriesEnum.SAP });
            }
          } else if (!pastSciMarkings && !isFirstToken) {
            const sciResult = collectSciMarks(value, tokens);
            if (!sciResult.ok) {
              classificationMark.warnings?.push(messages.invalidSci);
            } else {
              tokens = sciResult.val.tokens;
              if (!classificationMark.sci) classificationMark.sci = [];
              classificationMark.sci = [...classificationMark.sci, ...sciResult.val.marks];
              markOrder.push({ control: 'SCI', category: MarkCategoriesEnum.SCI });
            }
          } else {
            // catch all for any unrecognized marks
            if (!classificationMark.otherDissem) classificationMark.otherDissem = [];
            classificationMark.otherDissem?.push(value);
            markOrder.push({ control: value, category: MarkCategoriesEnum.SECONDARY_DISSEM });
          }
        }
        break;
    }

    leadingToken = tokens.shift();
  }

  const otherWarnings = [
    ...checkMarkOrder(markOrder),
    ...checkMarkCase(_mark),
    ...checkExtraSlashes(mark),
    ...checkEmptyControls(mark),
    ...checkInvalidCharacters(mark),
    ...checkHangingSlash(mark),
  ];

  const dedupedWarnings = [...new Set([...(classificationMark.warnings ?? []), ...otherWarnings])];
  return { ...classificationMark, warnings: dedupedWarnings };
}

export default parseClassification;
export {
  parseClassificationCategory,
  collectOrgs,
  checkMarkOrder,
  checkMarkCase,
  checkExtraSlashes,
  checkEmptyControls,
  checkInvalidCharacters,
  checkHangingSlash,
  messages,
};
